import { useCallback, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { IconArrowUpFromLineOutline18 as UploadIcon } from "nucleo-ui-outline-18"

import type { User } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { joinLandingPath } from "@productlane/lib/src/join-profile"
import { Avatar, Button, Input, Loader } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { AvatarCropDialog } from "@/components/avatar-crop-dialog"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { mutateOk, useZero } from "@/lib/zero"

export function JoinProfileScreen() {
	const [user] = useQuery(queries.currentUser())

	return (
		<main className="relative flex h-dvh w-full flex-col items-center justify-center bg-gray-100 p-6 dark:bg-gray-900">
			<MetaInfo title="Set up your profile" />
			{user ? <JoinProfileForm user={user} /> : null}
		</main>
	)
}

function JoinProfileForm({ user }: { user: User }) {
	const zero = useZero()
	const navigate = useNavigate()
	const next = useSearch({ strict: false, select: (s) => s.next })
	const getSignedUrlMutation = api.file.getUrl.useMutation()

	const [name, setName] = useState(user.name ?? "")
	const [nameError, setNameError] = useState<string | null>(null)
	const [imagePreview, setImagePreview] = useState<string | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [cropSource, setCropSource] = useState<{
		url: string
		fileName: string
		fileType: string
	} | null>(null)
	const imageInput = useRef<HTMLInputElement | null>(null)
	const image = imagePreview ?? user.imageUrl

	const goToInbox = useCallback(
		() => void navigate({ href: joinLandingPath(next), replace: true }),
		[navigate, next],
	)

	const uploadImage = useCallback(
		async (file: File, previewUrl: string) => {
			setImagePreview(previewUrl)
			setIsUploading(true)
			try {
				const { fileUrl, uploadUrl } = await getSignedUrlMutation.mutateAsync({
					fileName: file.name,
					fileType: file.type,
				})
				const res = await fetch(uploadUrl, {
					method: "PUT",
					body: file,
					headers: { "Content-Type": file.type },
				})
				if (!res.ok) throw new Error("File upload failed")
				await mutateOk(
					zero.mutate(
						mutators.users.update({ id: user.id, imageUrl: fileUrl }),
					),
				)
			} catch {
				setImagePreview(null)
				toast.error({
					title: "Your photo didn't upload",
					description: "Try again with the same image.",
					icon: UploadIcon,
				})
			} finally {
				setIsUploading(false)
			}
		},
		[getSignedUrlMutation, user.id, zero],
	)

	const save = useCallback(async () => {
		const trimmed = name.trim()
		if (!trimmed) {
			setNameError("Enter your name.")
			return
		}
		setIsSaving(true)
		try {
			if (trimmed !== user.name) {
				await mutateOk(
					zero.mutate(mutators.users.update({ id: user.id, name: trimmed })),
				)
			}
			analytics.track("complete-join-profile", { hasImage: !!image })
			goToInbox()
		} catch {
			toast.error({
				title: "Your name didn't save",
				description: "Try again.",
			})
		} finally {
			setIsSaving(false)
		}
	}, [goToInbox, image, name, user.id, user.name, zero])

	const skip = useCallback(() => {
		analytics.track("skip-join-profile")
		goToInbox()
	}, [goToInbox])

	return (
		<>
			<form
				className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl ring-1 ring-black/10 dark:bg-gray-850 dark:shadow-black dark:ring-white/10"
				onSubmit={(e) => {
					e.preventDefault()
					void save()
				}}
			>
				<h1 className="text-base font-semibold text-primary">
					Set up your profile
				</h1>
				<p className="mt-1 text-sm text-secondary">
					Teammates and customers see this name and photo when you reply.
				</p>
				<div className="mt-5 flex items-end gap-4">
					<button
						type="button"
						aria-label={image ? "Change your photo" : "Upload a photo"}
						className="group/avatar relative flex flex-none rounded-lg"
						disabled={isUploading}
						onClick={() => imageInput.current?.click()}
					>
						<Avatar name={name} image={image} size="xl" roundness="lg" />
						<span
							className={cn(
								"absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover/avatar:opacity-100",
								isUploading && "opacity-100",
							)}
						>
							{isUploading ? <Loader className="text-white" /> : null}
						</span>
						<span className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-black/15 dark:bg-gray-800 dark:ring-white/15">
							<UploadIcon className="h-3 w-3 flex-none" />
						</span>
					</button>
					<div className="min-w-0 flex-1">
						<Input
							name="join-profile-name"
							label="Full name"
							value={name}
							autoComplete="off"
							autoFocus
							maxLength={100}
							errorMessage={nameError}
							onFocus={(e) => e.currentTarget.select()}
							onChange={(e) => {
								setName(e.target.value)
								setNameError(null)
							}}
						/>
					</div>
				</div>
				<div className="mt-6 flex justify-end gap-2">
					<Button type="button" variant="ghost" onClick={skip}>
						Skip
					</Button>
					<Button type="submit" isLoading={isSaving || isUploading}>
						Continue
					</Button>
				</div>
			</form>
			<input
				name="join-profile-image"
				type="file"
				accept="image/*"
				ref={imageInput}
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0]
					if (file) {
						setCropSource({
							url: URL.createObjectURL(file),
							fileName: file.name,
							fileType: file.type,
						})
					}
					e.target.value = ""
				}}
			/>
			{cropSource ? (
				<AvatarCropDialog
					isOpen
					onOpenChange={(open) => {
						if (!open) {
							URL.revokeObjectURL(cropSource.url)
							setCropSource(null)
						}
					}}
					imageSrc={cropSource.url}
					fileName={cropSource.fileName}
					fileType={cropSource.fileType}
					onCropComplete={(croppedFile, previewUrl) => {
						void uploadImage(croppedFile, previewUrl)
					}}
				/>
			) : null}
		</>
	)
}
